import { useCallback, memo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import * as IconMDI from 'mdi-material-ui';
import useStyles from './styles';
import clsx from 'clsx';
import {
  hasDigit,
  hasLowerCaseLetter,
  hasSpecialCharacter,
  hasUpperCaseLetter,
  isAtLeastEightChars,
  notTooGuessable,
} from '../../utils/auth';

/**
 * PasswordFeedback Component
 *
 * Displays password requirement feedback with visual indicators (checkmarks/squares)
 * for each requirement. Used in password forms to provide real-time validation feedback.
 *
 * Requirements displayed:
 * - At least 8 characters
 * - 1 lowercase letter
 * - 1 uppercase letter
 * - 1 special character (not + or =)
 * - 1 number
 * - Not guessable (based on common password patterns)
 *
 * @param props - Component props
 * @param props.password - The password string to validate against requirements
 */
const PasswordFeedbackComponent = (props: PasswordFeedbackProps) => {
  const { classes } = useStyles();
  const { password } = props;

  /**
   * Renders a styled requirement text with icon indicator.
   * Shows a green checkmark if requirement is met, otherwise shows a square icon.
   *
   * @param text - The requirement text to display
   * @param active - Whether the requirement is satisfied
   * @returns Styled Typography component with icon indicator
   */
  const getStyledText = useCallback(
    (text: string, active: boolean) => {
      const Icon = active ? IconMDI.Check : IconMDI.SquareSmall;

      return (
        <Typography
          variant={'caption'}
          component={'span'}
          className={clsx(classes.feedbackRoot, {
            [classes.notSatisfiedFeedback]: !active,
            [classes.positiveFeedback]: active,
          })}
        >
          <span className={classes.feedbackIconContainer}>
            <Icon className={classes.checkmark} />
          </span>
          {text}
        </Typography>
      );
    },
    [classes],
  );

  return (
    <Box component={'span'} role={'status'} aria-label={'Password requirements feedback'}>
      <Typography variant={'caption'} color={'textPrimary'} component={'span'}>
        {'Password Requirements: '}
      </Typography>
      <Box component={'span'} sx={{ display: 'flex', flexDirection: 'column' }}>
        {getStyledText('At least 8 characters ', isAtLeastEightChars(password))}
        {getStyledText('1 lowercase letter ', hasLowerCaseLetter(password))}
        {getStyledText('1 uppercase letter ', hasUpperCaseLetter(password))}
        {getStyledText(
          '1 special character (not + or =) ',
          hasSpecialCharacter(password),
        )}
        {getStyledText('1 number ', hasDigit(password))}
        {getStyledText('Not guessable', notTooGuessable(password))}
      </Box>
    </Box>
  );
};

export type PasswordFeedbackProps = {
  /** The password string to validate against requirements */
  password: string;
};

/**
 * Memoized PasswordFeedback component.
 * Only re-renders when the password prop changes.
 */
const PasswordFeedback = memo(PasswordFeedbackComponent);

export default PasswordFeedback;
